<template>
  <div class="event-list" v-show="!loading">
    <card v-for="(game, idx) in safeGames" :key="'list-' + (game.id || idx)"
          :style="{ 'background-color': bss.card_color }"
          class="event-list__card">
      <template #raw-content>
        <div class="event-list__row"
             :class="{ 'event-list__row--img-right': bss.list_image_position === 2 }"
             :style="{ 'flex-direction': bss.list_image_position === 2 ? 'row-reverse' : 'row' }">
          <div v-if="isImageTop" class="event-list__left-col" :style="{ width: thumbWidth + 'px' }">
            <div class="event-list__thumb event-list__thumb--top" :style="{ width: thumbWidth + 'px', height: thumbHeightFor(game) + 'px' }">
              <template v-if="getGameImages(game).length > 0">
                <el-carousel :key="'LC-top-' + (game.id || idx) + '-' + getGameImages(game).length + (isMobileViewport ? '-m' : '-d')"
                             :arrow="getGameImages(game).length > 1 ? 'hover' : 'never'"
                             :autoplay="!!(bss.allow_autoscroll)"
                             indicator-position="none"
                             :interval="5000"
                             class="event-list__carousel">
                  <el-carousel-item v-for="(item, imgIdx) in getGameImages(game)"
                                    :key="'LCI' + game.id + '-' + imgIdx"
                                    :style="{ 'background-color': item.background_color }">
                    <div class="event-list__carousel-slide">
                      <img v-twin-src="item.url"
                           :alt="game.game_name"
                           class="event-list__img"
                           :style="{ 'transform': 'translate(0%, 0%) scale(' + (item.scale || 100) * 0.01 + ')',
                                     'left': (item.x || 0) + 'px',
                                     'top': (item.y || 0) + 'px',
                                     'object-fit': imgFitFor(game, item) }" />
                    </div>
                  </el-carousel-item>
                </el-carousel>
              </template>
              <div v-else class="event-list__img-placeholder"
                   :style="{ 'background-color': bss.background_color }">
                <i class="tim-icons icon-components"
                   :style="{ 'color': bss.text_color_card }"></i>
              </div>
            </div>
            <div v-if="bss.show_game_title || showMeta || showInfoTrigger(game)" class="event-list__info event-list__info--below">
              <h4 v-if="bss.show_game_title" class="event-list__name"
                  :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
                {{ game.game_name }}
              </h4>
              <div class="event-list__meta" v-if="showMeta"
                   :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
                <span v-if="bss.show_number_of_players" class="event-list__meta-item">
                  {{ $t('book.groupSize') }}: <strong>{{ game.min_players_count }}<span v-if="game.max_players_count"> - {{ game.max_players_count }}</span></strong>
                </span>
                <span v-if="bss.show_game_duration && game.duration_minutes" class="event-list__meta-item">
                  {{ $t('book.duration') }}: <strong>{{ game.duration_minutes }} {{ $t('book.minutes') }}</strong>
                </span>
                <event-ratings v-if="bss.show_game_difficulty" :ratings="game.ratings" />
              </div>
              <button v-if="showInfoTrigger(game)"
                      type="button"
                      class="event-list__info-trigger"
                      :style="{ color: bss.button_color, 'font-family': bss.font_family_card }"
                      :aria-label="$t('book.aboutEvent')"
                      :aria-expanded="isInfoOpen(game) ? 'true' : 'false'"
                      @click="toggleInfo(game)">
                <b-icon v-if="(bss.game_info_trigger_type || 'icon') === 'icon'" :icon="bss.game_info_trigger_icon || 'info-circle'" />
                <template v-else>{{ bss.game_info_trigger_text || 'Learn more' }}</template>
              </button>
            </div>
          </div>
          <template v-if="!isImageTop">
          <div class="event-list__thumb" :style="{ width: thumbWidth + 'px', 'min-height': thumbHeightFor(game) + 'px' }">
            <template v-if="getGameImages(game).length > 0">
              <el-carousel :key="'LC-side-' + (game.id || idx) + '-' + getGameImages(game).length + (isMobileViewport ? '-m' : '-d')"
                           :arrow="getGameImages(game).length > 1 ? 'hover' : 'never'"
                           :autoplay="!!(bss.allow_autoscroll)"
                           indicator-position="none"
                           :interval="5000"
                           class="event-list__carousel">
                <el-carousel-item v-for="(item, imgIdx) in getGameImages(game)"
                                  :key="'LCI' + game.id + '-' + imgIdx"
                                  :style="{ 'background-color': item.background_color }">
                  <div class="event-list__carousel-slide">
                    <img v-twin-src="item.url"
                         :alt="game.game_name"
                         class="event-list__img"
                         :style="{ 'transform': 'translate(0%, 0%) scale(' + (item.scale || 100) * 0.01 + ')',
                                   'left': (item.x || 0) + 'px',
                                   'top': (item.y || 0) + 'px',
                                   'object-fit': imgFitFor(game, item) }" />
                  </div>
                </el-carousel-item>
              </el-carousel>
            </template>
            <div v-else class="event-list__img-placeholder"
                 :style="{ 'background-color': bss.background_color }">
              <i class="tim-icons icon-components"
                 :style="{ 'color': bss.text_color_card }"></i>
            </div>
          </div>
          </template>
          <div v-if="!isImageTop && (bss.show_game_title || showMeta || showInfoTrigger(game))" class="event-list__info">
            <h4 v-if="bss.show_game_title" class="event-list__name"
                :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
              {{ game.game_name }}
            </h4>
            <div class="event-list__meta" v-if="showMeta"
                 :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
              <span v-if="bss.show_number_of_players" class="event-list__meta-item">
                {{ $t('book.groupSize') }}: <strong>{{ game.min_players_count }}<span v-if="game.max_players_count"> - {{ game.max_players_count }}</span></strong>
              </span>
              <span v-if="bss.show_game_duration && game.duration_minutes" class="event-list__meta-item">
                {{ $t('book.duration') }}: <strong>{{ game.duration_minutes }} {{ $t('book.minutes') }}</strong>
              </span>
              <event-ratings v-if="bss.show_game_difficulty" :ratings="game.ratings" />
            </div>
            <button v-if="showInfoTrigger(game)"
                    type="button"
                    class="event-list__info-trigger"
                    :style="{ color: bss.button_color, 'font-family': bss.font_family_card }"
                    :aria-label="$t('book.aboutEvent')"
                    :aria-expanded="isInfoOpen(game) ? 'true' : 'false'"
                    @click="toggleInfo(game)">
              <b-icon v-if="(bss.game_info_trigger_type || 'icon') === 'icon'" :icon="bss.game_info_trigger_icon || 'info-circle'" />
              <template v-else>{{ bss.game_info_trigger_text || 'Learn more' }}</template>
            </button>
          </div>
          <div class="event-list__slots">
            <div v-if="!game || !game.slots || getOrderedTimeSlots(game).length === 0"
                 class="event-list__no-slots"
                 :style="{ 'color': bss.text_color_card, 'font-family': bss.font_family_card }">
              {{ $t('book.noBookings') }}
            </div>
            <div v-else class="event-list__slot-grid">
              <button v-for="(s, i) in getOrderedTimeSlots(game)" :key="'LS' + game.id + '-' + i"
                      class="event-list__slot-btn"
                      :class="getSlotClass(game, s)"
                      :style="getSlotStyle(game, s)"
                      :disabled="isSlotDisabled(game, s)"
                      @click="handleSlotClick(game, s)">
                <span v-if="shouldShowSlotBadge(s, game)" class="event-list__slot-badge"
                      :style="{ background: bss.button_color, color: bss.background_color || '#fff' }">{{ s.custom_message }}</span>
                <span class="event-list__slot-time">{{ getDisplayTime(s) }}</span>
                <span v-if="showTicketCount(game, s)" class="event-list__slot-tickets">
                  {{ getRemainingTickets(game, s) }}/{{ game.max_players_count }} {{ $t('book.availableTickets') }}
                </span>
                <span v-if="getSlotLabel(game, s)" class="event-list__slot-label">
                  {{ getSlotLabel(game, s) }}
                </span>
                <span v-if="shouldUseCustomText(s, game)" class="event-list__slot-label event-list__slot-label--custom">
                  {{ s.custom_message }}
                </span>
              </button>
            </div>
            <div v-if="bss.show_calendar_link && getOrderedTimeSlots(game).length > 0" class="event-list__see-more">
              <base-button :style="{'font-family': bss.font_family_card, 'color': bss.text_color_card}" link @click="$emit('show-calendar', game)">
                {{ $t('book.buttons.seeMoreTimes') }} >
              </base-button>
            </div>
          </div>
        </div>
        <div v-if="isInfoOpen(game)"
             class="event-list__description"
             :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }"
             v-html="resolvedGameDescription(game)"></div>
      </template>
    </card>

    <!-- Gift card row -->
    <card v-if="isGiftCard"
          :style="{ 'background-color': bss.card_color }"
          class="event-list__card">
      <template #raw-content>
        <div class="event-list__row"
             :class="{ 'event-list__row--img-right': bss.list_image_position === 2 }"
             :style="{ 'flex-direction': bss.list_image_position === 2 ? 'row-reverse' : 'row' }">
          <div v-if="isImageTop" class="event-list__left-col" :style="{ width: thumbWidth + 'px' }">
            <div class="event-list__thumb event-list__thumb--top" :style="{ width: thumbWidth + 'px', height: thumbHeight + 'px' }">
              <img v-if="giftCardImage"
                   :src="giftCardImage"
                   alt="Gift card"
                   class="event-list__img" />
              <div v-else class="event-list__img-placeholder"
                   :style="{ 'background-color': bss.background_color }">
                <i class="tim-icons icon-tag"
                   :style="{ 'color': bss.text_color_card }"></i>
              </div>
            </div>
            <div class="event-list__info event-list__info--below">
              <h4 class="event-list__name"
                  :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
                {{ giftCardSettings.title || 'Gift cards' }}
              </h4>
              <div v-if="giftCardSettings.subtext" class="event-list__meta"
                   :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
                {{ giftCardSettings.subtext }}
              </div>
            </div>
          </div>
          <template v-if="!isImageTop">
          <div class="event-list__thumb" :style="{ width: thumbWidth + 'px', 'min-height': thumbHeight + 'px' }">
            <img v-if="giftCardImage"
                 :src="giftCardImage"
                 alt="Gift card"
                 class="event-list__img" />
            <div v-else class="event-list__img-placeholder"
                 :style="{ 'background-color': bss.background_color }">
              <i class="tim-icons icon-tag"
                 :style="{ 'color': bss.text_color_card }"></i>
            </div>
          </div>
          <div class="event-list__info">
            <h4 class="event-list__name"
                :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
              {{ giftCardSettings.title || 'Gift cards' }}
            </h4>
            <div v-if="giftCardSettings.subtext" class="event-list__meta"
                 :style="{ 'font-family': bss.font_family_card, 'color': bss.text_color_card }">
              {{ giftCardSettings.subtext }}
            </div>
          </div>
          </template>
          <div class="event-list__slots">
            <button class="event-list__slot-btn event-list__slot-btn--active"
                    :style="{ 'border-color': bss.button_color,
                               'color': bss.button_color,
                               'font-family': bss.font_family_card }"
                    @click="$emit('gift-card-select')">
              {{ giftCardSettings.button_text || $t('book.buttons.select') }}
            </button>
          </div>
        </div>
      </template>
    </card>
  </div>
</template>

<script>
import { time } from '@/plugins/dateFormatter'
import { getNow } from '@/util/serverTime'
import { parseGameImages, MOBILE_BREAKPOINT } from '@/util/gameImages'
import gameImageOrientation from '@/mixins/gameImageOrientation'
import _ from 'lodash'
import { Carousel, CarouselItem } from 'element-ui'
import EventRatings from '@/components/EventRatings.vue'

export default {
  name: 'EventSelectionList',
  mixins: [gameImageOrientation],
  components: {
    [Carousel.name]: Carousel,
    [CarouselItem.name]: CarouselItem,
    EventRatings
  },
  props: {
    games: { type: [Array, String], default: () => [] },
    bss: { type: Object, default: () => ({}) },
    locale: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    isGiftCard: { type: Boolean, default: false },
    giftCardLink: { type: Boolean, default: false },
    giftCardSettings: { type: Object, default: () => ({}) },
    giftCardImage: { type: String, default: null }
  },
  data() {
    return {
      isAligning: false,
      windowWidth: window.innerWidth,
      expandedInfoIds: []
    }
  },
  mounted() {
    this._onResize = () => { this.windowWidth = window.innerWidth }
    window.addEventListener('resize', this._onResize)
    this.resizeObserver = new ResizeObserver(_.debounce(() => {
      this.recalculate()
    }, 150))
    this.resizeObserver.observe(this.$el)
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  watch: {
    safeGames: {
      immediate: true,
      handler() {
        this.$nextTick(() => this.recalculate())
        this.probeOrientations()
      }
    },
    // The mobile filter can swap which image is first, so re-probe on breakpoint change.
    isMobileViewport() {
      this.probeOrientations()
    }
  },
  methods: {
    // The expand panel shows the short booking-page highlight, falling back to the full
    // event description when the highlight is blank. Mirrors CreateBooking's grid view.
    resolvedGameDescription(game) {
      const short = game.game_short_description || ''
      const hasShort = short.replace(/<[^>]*>/g, '').trim() !== '' || /<img\b/i.test(short)
      return hasShort ? short : (game.game_description || '')
    },
    hasGameDescription(game) {
      const html = this.resolvedGameDescription(game)
      return html.replace(/<[^>]*>/g, '').trim() !== '' || /<img\b/i.test(html)
    },
    showInfoTrigger(game) {
      return !!this.bss.show_game_description && this.hasGameDescription(game)
    },
    isInfoOpen(game) {
      return this.expandedInfoIds.includes(game.game_id || game.id)
    },
    toggleInfo(game) {
      const id = game.game_id || game.id
      if (this.expandedInfoIds.includes(id)) {
        this.expandedInfoIds = this.expandedInfoIds.filter(x => x !== id)
      } else {
        this.expandedInfoIds = [...this.expandedInfoIds, id]
      }
    },
    recalculate() {
      this.$nextTick(() => this.alignCardHeights())
    },
    alignCardHeights() {
      if (this.isAligning) return
      this.isAligning = true
      const rows = this.$el.querySelectorAll('.event-list__row')
      if (!rows.length) { this.isAligning = false; return }

      // Reset any previously applied fixed heights so we measure natural sizes
      rows.forEach(row => { row.style.height = '' })

      this.$nextTick(() => {
        const games = this.safeGames
        // Group game rows by actual rendered grid row count
        const groups = {}
        rows.forEach((row, i) => {
          if (i >= games.length) return // gift card row — skip
          const grid = row.querySelector('.event-list__slot-grid')
          let gridRowCount = 0
          if (grid) {
            const gridRows = window.getComputedStyle(grid).gridTemplateRows
            gridRowCount = gridRows ? gridRows.split(' ').length : 0
          }
          if (!groups[gridRowCount]) groups[gridRowCount] = []
          groups[gridRowCount].push({ index: i, height: row.getBoundingClientRect().height })
        })

        // Apply max height per group
        Object.values(groups).forEach(group => {
          if (group.length < 2) return
          const maxH = Math.max(...group.map(g => g.height))
          group.forEach(g => { rows[g.index].style.height = maxH + 'px' })
        })

        this.isAligning = false
      })
    },
    // Unlike the fixed-box surfaces, the list thumb GROWS to 3:4 when the first
    // displayed image is portrait; other slides that then mismatch the box are
    // contained. isNeutralImage/probeImageOrientation/portraitImages come from
    // the gameImageOrientation mixin.
    probeOrientations() {
      this.safeGames.forEach(game => {
        this.probeImageOrientation(this.getGameImages(game))
      })
    },
    thumbIsPortrait(game) {
      const imgs = this.getGameImages(game)
      return !!(imgs.length && this.isNeutralImage(imgs[0]) && this.portraitImages[imgs[0].url])
    },
    thumbHeightFor(game) {
      return this.thumbIsPortrait(game) ? Math.round(this.thumbWidth * 4 / 3) : this.thumbHeight
    },
    // A neutral slide whose orientation mismatches the thumb box renders
    // contained (letterboxed on its background color) instead of cover-cropped.
    imgFitFor(game, item) {
      const imgPortrait = this.portraitImages[item.url]
      if (imgPortrait === undefined || !this.isNeutralImage(item)) return item.object_fit || 'cover'
      return imgPortrait === this.thumbIsPortrait(game) ? (item.object_fit || 'cover') : 'contain'
    },
    getGameImages(game) {
      return parseGameImages(game.game_images, this.windowWidth <= MOBILE_BREAKPOINT)
    },
    getOrderedTimeSlots(game) {
      let s = []
      const slots = JSON.parse(game.slots)

      if (this.bss.show_unavailable_slots === 0) {
        for (let n in slots) {
          // Custom-status slots: visibility comes from online_display_mode, not
          // show_unavailable_slots. 'hidden' never shows; 'bookable'/'non_bookable' always show.
          if (slots[n].online_display_mode === 'hidden') continue
          if (slots[n].online_display_mode === 'bookable' || slots[n].online_display_mode === 'non_bookable') {
            s.push(slots[n])
            continue
          }
          // Claimed by a pending request: hides exactly as a booked private slot.
          if (slots[n].status === 'requested') continue
          // Requestable with the template toggle off, or on a public master
          // (requests are private-only): hides exactly as blocked.
          if (slots[n].status === 'requestable'
            && (Number(this.bss.allow_request_to_book) !== 1 || this.isPublicBooking(game, slots[n]))) continue
          if (this.isPublicBooking(game, slots[n])
            || (slots[n].status !== 'booked' && slots[n].status !== 'blocked')) {
            s.push(slots[n])
          }
        }
      } else {
        // Show everything except hidden custom statuses, which must never appear online.
        s = (slots || []).filter(slot => slot.online_display_mode !== 'hidden')
      }

      if (s === null) {
        s = []
      }

      let blocked_days = 0
      let blocked_minutes = 0
      let slot_date = new Date()

      for (let n in s) {
        slot_date = new Date()
        // Start from the 1st: the date begins as today, and setting the month while on
        // the 29th-31st overflows into the following month when the target month is shorter
        slot_date.setDate(1)
        slot_date.setFullYear(s[n].booking_date.split('-')[0])
        slot_date.setMonth(parseInt(s[n].booking_date.split('-')[1]) - 1)
        slot_date.setDate(s[n].booking_date.split('-')[2])
        slot_date.setHours(s[n].start_time.split(':')[0])
        slot_date.setMinutes(s[n].start_time.split(':')[1])
        blocked_days = 0
        blocked_minutes = 0
        let unblock_date = getNow()

        // Min booking period — sets s[n].block for call-to-book
        if (s[n].allow_late_booking === 1) {
          unblock_date = new Date(slot_date.getTime())
          if (s[n].min_booking_period === 0) {
            blocked_minutes = s[n].min_booking_value
            unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
          } else if (s[n].min_booking_period === 1) {
            blocked_minutes = parseInt(s[n].min_booking_value) * 60
            unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
          } else if (s[n].min_booking_period === 2) {
            blocked_days = s[n].min_booking_value
            unblock_date.setDate(unblock_date.getDate() + blocked_days)
          } else if (s[n].min_booking_period === 3) {
            blocked_days = 7 * parseInt(s[n].min_booking_value)
            unblock_date.setDate(unblock_date.getDate() + blocked_days)
          } else if (s[n].min_booking_period === 4) {
            const slotDate = new Date(unblock_date.getTime())
            const futureDate = new Date(slotDate.getTime())
            futureDate.setMonth(futureDate.getMonth() + parseInt(s[n].min_booking_value))
            const timeDifference = futureDate.getTime() - slotDate.getTime()
            blocked_days = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
            unblock_date.setDate(unblock_date.getDate() + blocked_days)
          } else if (s[n].min_booking_period === null) {
            unblock_date = new Date(slot_date.getTime())
          }
        } else {
          if (s[n].min_booking_period === 0) {
            blocked_minutes = s[n].min_booking_value
            unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
          } else if (s[n].min_booking_period === 1) {
            blocked_minutes = parseInt(s[n].min_booking_value) * 60
            unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
          } else if (s[n].min_booking_period === 2) {
            blocked_days = s[n].min_booking_value
            unblock_date.setDate(unblock_date.getDate() + blocked_days)
          } else if (s[n].min_booking_period === 3) {
            blocked_days = 7 * parseInt(s[n].min_booking_value)
            unblock_date.setDate(unblock_date.getDate() + blocked_days)
          } else if (s[n].min_booking_period === 4) {
            const currentDate = getNow()
            const futureDate = new Date(currentDate.getTime())
            futureDate.setMonth(futureDate.getMonth() + parseInt(s[n].min_booking_value))
            const timeDifference = futureDate.getTime() - currentDate.getTime()
            blocked_days = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
            unblock_date.setDate(unblock_date.getDate() + blocked_days)
          } else if (s[n].min_booking_period === null) {
            unblock_date = getNow()
          }
        }

        if (s[n].allow_late_booking === 1) {
          const currentTime = getNow()
          s[n].block = currentTime > unblock_date
        } else {
          s[n].block = slot_date <= unblock_date
        }

        // Max booking period — sets s[n].remove for slots too far in the future
        let max_days = 0
        if (s[n].max_booking_period === 0) {
          max_days = parseInt(s[n].max_booking_value)
        } else if (s[n].max_booking_period === 1) {
          max_days = parseInt(s[n].max_booking_value) * 7
        } else if (s[n].max_booking_period === 2) {
          let future = getNow()
          let today = getNow()
          future.setMonth(future.getMonth() + parseInt(s[n].max_booking_value))
          let diffTime = Math.abs(future - today)
          max_days = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        } else if (s[n].max_booking_period === 3) {
          let future = getNow()
          let today = getNow()
          future.setFullYear(future.getFullYear() + parseInt(s[n].max_booking_value))
          let diffTime = Math.abs(future - today)
          max_days = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        } else if (s[n].max_booking_period === null) {
          max_days = 10
        }

        let max_date = getNow()
        max_date.setDate(max_date.getDate() + max_days)

        if (slot_date >= max_date) {
          s[n].remove = true
        } else {
          s[n].remove = false
        }

        // When the slot has crossed its minimum-notice (or late-booking) window and the
        // schedule opted to hide rather than call-to-book, drop it from the page entirely.
        if (s[n].block && Number(s[n].hide_past_min_notice) === 1) {
          s[n].remove = true
        }
      }

      const visibleSlots = s.filter(slot => !slot.remove)
      return _.orderBy(visibleSlots, 'start_time')
    },
    isPublicBooking(game, slot) {
      if (slot.is_custom_pricing_enabled) {
        return slot.is_public_booking
      } else {
        return game.is_public_booking
      }
    },
    hasRemainingSlots(game, slot) {
      const playerHeadroom = game.max_players_count - slot.group_size
      if (slot.resource_max_remaining_seats != null) {
        return Math.min(playerHeadroom, slot.resource_max_remaining_seats) > 0
      }
      return playerHeadroom > 0
    },
    getRemainingTickets(game, slot) {
      const playerHeadroom = game.max_players_count - slot.group_size
      if (slot.resource_max_remaining_seats != null) {
        return Math.min(playerHeadroom, slot.resource_max_remaining_seats)
      }
      return playerHeadroom
    },
    shouldSlotBeCallToBook(slot) {
      const currentTime = getNow()
      let slot_date = new Date()
      // Start from the 1st: the date begins as today, and setting the month while on
      // the 29th-31st overflows into the following month when the target month is shorter
      slot_date.setDate(1)
      slot_date.setFullYear(slot.booking_date.split('-')[0])
      slot_date.setMonth(parseInt(slot.booking_date.split('-')[1]) - 1)
      slot_date.setDate(slot.booking_date.split('-')[2])
      slot_date.setHours(slot.start_time.split(':')[0])
      slot_date.setMinutes(slot.start_time.split(':')[1])

      let blocked_days = 0
      let blocked_minutes = 0
      let unblock_date = getNow()

      if (slot.allow_late_booking === 1) {
        unblock_date = new Date(slot_date.getTime())
        if (slot.min_booking_period === 0) {
          blocked_minutes = slot.min_booking_value
          unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
        } else if (slot.min_booking_period === 1) {
          blocked_minutes = parseInt(slot.min_booking_value) * 60
          unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
        } else if (slot.min_booking_period === 2) {
          blocked_days = slot.min_booking_value
          unblock_date.setDate(unblock_date.getDate() + blocked_days)
        } else if (slot.min_booking_period === 3) {
          blocked_days = 7 * parseInt(slot.min_booking_value)
          unblock_date.setDate(unblock_date.getDate() + blocked_days)
        } else if (slot.min_booking_period === 4) {
          const slotDate = new Date(unblock_date.getTime())
          const futureDate = new Date(slotDate.getTime())
          futureDate.setMonth(futureDate.getMonth() + parseInt(slot.min_booking_value))
          const timeDifference = futureDate.getTime() - slotDate.getTime()
          blocked_days = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
          unblock_date.setDate(unblock_date.getDate() + blocked_days)
        } else if (slot.min_booking_period === null) {
          unblock_date = new Date(slot_date.getTime())
        }
      } else {
        if (slot.min_booking_period === 0) {
          blocked_minutes = slot.min_booking_value
          unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
        } else if (slot.min_booking_period === 1) {
          blocked_minutes = parseInt(slot.min_booking_value) * 60
          unblock_date.setMinutes(unblock_date.getMinutes() + blocked_minutes)
        } else if (slot.min_booking_period === 2) {
          blocked_days = slot.min_booking_value
          unblock_date.setDate(unblock_date.getDate() + blocked_days)
        } else if (slot.min_booking_period === 3) {
          blocked_days = 7 * parseInt(slot.min_booking_value)
          unblock_date.setDate(unblock_date.getDate() + blocked_days)
        } else if (slot.min_booking_period === 4) {
          const currentDate = getNow()
          const futureDate = new Date(currentDate.getTime())
          futureDate.setMonth(futureDate.getMonth() + parseInt(slot.min_booking_value))
          const timeDifference = futureDate.getTime() - currentDate.getTime()
          blocked_days = Math.ceil(timeDifference / (1000 * 60 * 60 * 24))
          unblock_date.setDate(unblock_date.getDate() + blocked_days)
        } else if (slot.min_booking_period === null) {
          unblock_date = getNow()
        }
      }

      if (slot.allow_late_booking === 1) {
        if (currentTime >= slot_date) {
          return currentTime > unblock_date
        } else {
          return false
        }
      } else {
        return slot_date <= unblock_date
      }
    },
    getDisplayTime(slot) {
      if (slot.is_all_day) {
        return slot.all_day_text || this.$t('book.allDay')
      }
      return time(this.locale.time_format, slot.start_time)
    },
    getSlotType(game, s) {
      // Resource-saturated slots render as sold-out regardless of underlying status.
      if (s.is_resource_saturated) {
        return 'sold-out'
      }
      // A custom status shown online as Sold out renders exactly as a sold out
      // slot: site sold out text and colors, no per-slot text or link. bookable
      // behaves like a normal available slot until booked.
      if (s.online_display_mode === 'non_bookable') return 'sold-out'
      if (s.online_display_mode === 'bookable' && s.status !== 'booked' && !s.block && !s.remove) {
        return this.isPublicBooking(game, s) ? 'available-public' : 'available'
      }
      // Claimed by a pending booking request: other viewers see it exactly as
      // a booked private slot.
      if (s.status === 'requested') {
        return 'sold-out'
      }
      // Request-to-book classification (mirror of slotDisplay.getSlotType).
      // A requestable slot offers a request button until its minimum notice
      // passes, then follows its own three-way past-notice behavior; an
      // available slot past its notice with request_past_min_notice set does
      // the same. A closed late-booking window is never requestable.
      const allowRtb = Number(this.bss && this.bss.allow_request_to_book) === 1
      if (s.status === 'requestable') {
        // Requests are private-slot only; a public master marked requestable
        // is not sellable and renders blocked.
        if (!allowRtb || this.isPublicBooking(game, s)) return 'blocked'
        if (!s.remove && !s.block) return 'requestable'
        if (!s.remove && s.block && s.allow_late_booking !== 1 && Number(s.request_past_min_notice) === 1) return 'requestable'
        if (s.block && !s.has_external_link) return 'call-to-book'
        return 'blocked'
      }
      if (allowRtb && s.status === 'available' && s.block && !s.remove
        && s.allow_late_booking !== 1 && Number(s.request_past_min_notice) === 1
        && !s.has_external_link && !this.isPublicBooking(game, s)) {
        return 'requestable'
      }
      // Available (private)
      if ((!s.block && !s.remove) && s.status === 'available' && !this.isPublicBooking(game, s)) {
        return 'available'
      }
      // Available (public)
      if ((!s.block && !s.remove) && s.status === 'available' && this.isPublicBooking(game, s)) {
        return 'available-public'
      }
      // Booked (sold out or private)
      if (s.status === 'booked' && (s.is_reservation_upgraded || !this.isPublicBooking(game, s) || (this.isPublicBooking(game, s) && !this.hasRemainingSlots(game, s)))) {
        return 'sold-out'
      }
      // Booked public with remaining slots - call to book
      if (s.status === 'booked' && !s.is_reservation_upgraded && this.isPublicBooking(game, s) && this.hasRemainingSlots(game, s) && this.shouldSlotBeCallToBook(s)) {
        return 'call-to-book'
      }
      // Booked public with remaining slots - available
      if (s.status === 'booked' && !s.is_reservation_upgraded && this.isPublicBooking(game, s) && this.hasRemainingSlots(game, s) && !this.shouldSlotBeCallToBook(s)) {
        return 'available-public'
      }
      // Blocked
      if (s.status === 'blocked') {
        return 'blocked'
      }
      // Call to book (with external link)
      if ((s.status === 'call_to_book' || s.block) && s.has_external_link && !(s.status === 'booked' || s.status === 'blocked')) {
        return 'call-to-book-link'
      }
      // Call to book (no external link, with slot text)
      if ((s.status === 'call_to_book' && !s.remove || s.block && !s.remove) && !s.has_external_link && s.slot_text && !(s.status === 'booked' || s.status === 'blocked')) {
        return 'call-to-book'
      }
      // Call to book (no external link, no slot text)
      if ((s.status === 'call_to_book' || s.block) && !s.has_external_link && !s.slot_text && !(s.status === 'booked' || s.status === 'blocked')) {
        return 'call-to-book'
      }
      return 'unknown'
    },
    getSlotClass(game, s) {
      const type = this.getSlotType(game, s)
      const isDisabled = type === 'sold-out' || type === 'blocked' || type === 'call-to-book'
      return {
        'event-list__slot-btn--active': !isDisabled,
        'event-list__slot-btn--disabled': isDisabled
      }
    },
    getSlotStyle(game, s) {
      const type = this.getSlotType(game, s)
      const isDisabled = type === 'sold-out' || type === 'blocked' || type === 'call-to-book'
      const style = {
        'font-family': this.bss.font_family_card,
        'border-color': this.bss.button_color,
        'color': this.bss.button_color
      }

      if (isDisabled) {
        style['opacity'] = '0.45'
      }

      return style
    },
    isSlotDisabled(game, s) {
      const type = this.getSlotType(game, s)
      return type === 'sold-out' || type === 'blocked' || type === 'call-to-book'
    },
    handleSlotClick(game, s) {
      const type = this.getSlotType(game, s)
      if (type === 'call-to-book-link' && s.has_external_link) {
        const w = window.open(s.external_link, '_blank')
        if (w) w.focus()
        return
      }
      if (type === 'available' || type === 'available-public' || type === 'requestable') {
        this.$emit('slot-selected', { game, slot: s })
      }
    },
    showTicketCount(game, s) {
      const type = this.getSlotType(game, s)
      return this.bss.show_available_ticket_count && (type === 'available-public')
    },
    getSlotLabel(game, s) {
      const type = this.getSlotType(game, s)
      // Deliberately NOT s.slot_text — that carries call-to-book wording.
      if (type === 'requestable') return this.bss.request_to_book_text || this.$t('book.requestToBook')
      if (type === 'sold-out') return this.bss.booked_text || ''
      if (type === 'blocked') return this.bss.blocked_text || ''
      if (type === 'call-to-book' || type === 'call-to-book-link') {
        return s.slot_text || this.bss.call_to_book_text || ''
      }
      return ''
    },
    shouldShowSlotBadge(s, game) {
      if (!s.custom_message || !s.custom_message_active || s.custom_message_display !== 'badge') return false
      if (this.bss.hide_custom_message_when_sold_out && this.getSlotType(game, s) === 'sold-out') return false
      return true
    },
    shouldUseCustomText(s, game) {
      if (!s.custom_message || !s.custom_message_active || s.custom_message_display !== 'text') return false
      if (this.bss.hide_custom_message_when_sold_out && this.getSlotType(game, s) === 'sold-out') return false
      return true
    }
  },
  computed: {
    safeGames() {
      if (this.giftCardLink) return []
      return Array.isArray(this.games) ? this.games : []
    },
    isMobileViewport() {
      return this.windowWidth <= 576
    },
    thumbWidth() {
      const isMobile = this.windowWidth <= 576
      const sizeMap = isMobile
        ? { 1: 70, 2: 90, 3: 110, 4: 140 }
        : { 1: 160, 2: 230, 3: 310, 4: 400 }
      return sizeMap[this.bss.list_image_size] || sizeMap[2]
    },
    thumbHeight() {
      const isMobile = this.windowWidth <= 576
      const sizeMap = isMobile
        ? { 1: 60, 2: 75, 3: 90, 4: 110 }
        : { 1: 120, 2: 170, 3: 220, 4: 280 }
      return sizeMap[this.bss.list_image_size] || sizeMap[2]
    },
    isImageTop() {
      return this.bss.list_image_position === 3
    },
    showMeta() {
      return this.bss.show_number_of_players || this.bss.show_game_duration || this.bss.show_game_difficulty
    }
  }
}
</script>

<style scoped>
.event-list {
  width: 100%;
  padding: 0 10px;
}

.event-list__card {
  margin-bottom: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.event-list__card:last-child {
  margin-bottom: 0;
}

.event-list__row {
  display: flex;
  align-items: stretch;
}

.event-list__thumb {
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 0.25rem 0 0 0.25rem;
  position: relative;
  max-width: 40%;
}

.event-list__thumb .event-list__img-placeholder {
  border-radius: 0;
}

.event-list__carousel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.event-list__carousel >>> .el-carousel__container {
  height: 100% !important;
}

.event-list__carousel >>> .el-carousel__arrow {
  width: 24px;
  height: 24px;
  font-size: 10px;
}

.event-list__carousel-slide {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.event-list__carousel-slide .event-list__img {
  position: absolute;
  transform-origin: center center;
  will-change: transform;
}

.event-list__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.event-list__img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
}

.event-list__info {
  flex-shrink: 0;
  width: 30%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 18px 20px;
  min-width: 0;
}

.event-list__name {
  font-size: clamp(16px, 2.5vw, 24px);
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 0;
}

.event-list__slots {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 18px 20px 18px 0;
}

.event-list__thumb + .event-list__slots,
.event-list__left-col + .event-list__slots {
  padding-left: 20px;
}

.event-list__no-slots {
  font-size: 13px;
  opacity: 0.6;
}

.event-list__slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  width: 100%;
}

.event-list__slot-btn {
  position: relative;
  background: transparent;
  border: 2px solid;
  border-radius: 0.4285rem;
  padding: 11px 22px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  line-height: 1.35;
  transition: opacity 0.15s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.event-list__slot-badge {
  position: absolute;
  top: -8px;
  right: 6px;
  z-index: 2;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  max-width: calc(100% - 12px);
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  line-height: 1.2;
}

.event-list__slot-label.event-list__slot-label--custom {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  word-break: normal;
  overflow-wrap: break-word;
}

/* Active slots: border + text color from button_color */
.event-list__slot-btn--active {
  border-color: inherit;
  color: inherit;
}

.event-list__slot-btn--active:hover {
  opacity: 0.85;
}

/* Disabled slots: same color, dimmed */
.event-list__slot-btn--disabled {
  cursor: default;
  border-color: inherit;
  color: inherit;
}

.event-list__slot-time {
  display: block;
  white-space: nowrap;
  font-size: 1rem;
  font-weight: 700;
}

.event-list__slot-tickets {
  display: block;
  font-size: 60%;
  font-weight: 400;
  opacity: 0.85;
  margin-top: 2px;
}

.event-list__slot-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

.event-list__see-more {
  text-align: center;
  margin-top: 6px;
}

.event-list__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
  font-size: 0.875rem;
  opacity: 0.75;
}

/* Rating icons in the list are a little smaller and tighter than the text, so five fit the
   narrow info column on a phone. */
.event-list__meta ::v-deep .event-ratings__icons {
  font-size: 0.9em;
  gap: 1px;
}

.event-list__info-trigger {
  background: none;
  border: none;
  padding: 4px 0;
  margin-top: 2px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
}

.event-list__description {
  padding: 10px 14px 12px;
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: left;
  word-break: normal;
  overflow-wrap: break-word;
}

.event-list__description ::v-deep img {
  max-width: 100%;
  height: auto;
}

.event-list__description ::v-deep p:last-child {
  margin-bottom: 0;
}

/* Image on top (stacked left column) */
.event-list__left-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.event-list__thumb--top {
  border-radius: 0.25rem 0 0 0;
  max-width: none;
}

.event-list__info--below {
  width: 100% !important;
  padding: 8px 12px;
}

/* Image on right */
.event-list__row--img-right .event-list__thumb {
  border-radius: 0 0.25rem 0.25rem 0;
}
.event-list__row--img-right .event-list__slots {
  padding: 18px 0 18px 20px;
}
.event-list__row--img-right .event-list__thumb + .event-list__slots {
  padding-right: 20px;
}

/* Tablet */
@media (max-width: 768px) {
  .event-list__info {
    padding: 10px 12px;
  }

  .event-list__slots {
    padding: 10px 12px 10px 0;
  }

  .event-list__thumb + .event-list__slots {
    padding-left: 12px;
  }

  .event-list__row--img-right .event-list__thumb + .event-list__slots {
    padding-right: 12px;
  }

  .event-list__slot-btn {
    padding: 6px 10px;
    font-size: 12px;
  }
}

/* Mobile */
@media (max-width: 576px) {
  .event-list__info {
    width: auto;
    flex: 1;
    padding: 8px 10px;
  }

  .event-list__slots {
    flex: 2;
    padding: 8px 10px 8px 0;
  }

  .event-list__row--img-right .event-list__slots {
    padding: 8px 0 8px 10px;
  }

  .event-list__slot-grid {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
    gap: 4px;
  }

  .event-list__slot-btn {
    padding: 5px 8px;
    font-size: 11px;
  }
}
</style>
